import type { AdminBlastDoc } from '@bb/domain/dist/schemas';
import { Color, Flex } from '@bb/elements-web';
import type { AnnouncementType } from 'api/AnnouncementController';
import React, { useState } from 'react';
import styled from 'styled-components';
import AnnouncementItemActions from './AnnouncementItemActions';
import AnnouncementItemDetails from './AnnouncementItemDetails';

type AnnouncementItemProps = {
  announcement: AdminBlastDoc & { userName?: string };
  type: AnnouncementType;
  deleteAnnouncement: (
    id: string,
  ) => (event: React.MouseEvent) => Promise<void>;
};

const AnnouncementItem = ({
  announcement,
  type,
  deleteAnnouncement,
}: AnnouncementItemProps) => {
  const { id, subject, dateSent, userName, body, delay, updatedAt } =
    announcement;
  const [isPreview, setIsPreview] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  let time = type === 'draft' ? updatedAt : dateSent;
  if (type === 'scheduled' && delay) {
    time = new Date(delay.dateToSend);
  }

  return (
    <AnnouncementItemStyled
      $isPreview={isPreview}
      $isMenuOpen={isMenuOpen}
      onClick={() => setIsPreview(prev => !prev)}
      data-cy={`announcement-item-${id}`}
    >
      <ThumbnailContainer />
      <AnnouncementItemDetails
        subject={subject}
        type={type}
        time={time}
        userName={userName}
        body={body}
      />
      <AnnouncementItemActions
        id={id}
        type={type}
        onOpenChange={setIsMenuOpen}
        deleteAnnouncement={deleteAnnouncement}
      />
    </AnnouncementItemStyled>
  );
};

const AnnouncementItemStyled = styled(Flex)<{
  $isPreview: boolean;
  $isMenuOpen: boolean;
}>`
  flex: 1 0 100%;
  border-top: solid 1px ${Color.LIGHT_GREY};
  border-bottom: solid 1px ${Color.LIGHT_GREY};
  padding: 0.5em 0 0.5em 0;
  transition: all 0.3s ease-out;
  overflow: hidden;
  max-height: 9em;
  min-height: 9em;
  height: auto;

  &&:hover {
    background-color: rgba(0, 0, 0, 0.03);
    max-height: 11em;
    cursor: pointer;
  }

  ${props =>
    props.$isMenuOpen &&
    `&& {
      background-color: rgba(0, 0, 0, 0.03);
      max-height: 11em;
    }
  `}

  ${props =>
    props.$isPreview &&
    `&&& {
      background-color: rgba(0, 0, 0, 0.05);
      max-height: 100vh;
    }
  `}
`;

const ThumbnailContainer = styled.div`
  flex: 1 2 15%;
`;

export default AnnouncementItem;
